import {
  Listbox,
  ListboxButton,
  ListboxOption,
  ListboxOptions,
} from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import classnames from 'classnames';
import { Formik } from 'formik';
import _ from 'lodash';
import React from 'react';
import Modal from 'react-modal';
import useSWR from 'swr';
import * as Yup from 'yup';

import {
  EmbeddedCheckout,
  EmbeddedCheckoutProvider,
} from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';

import api, { swrFetcher } from 'utils/api';
import districts from 'utils/districts';

import { useUserAuth } from 'contexts/UserAuth';

const { useState, useRef } = React;
const stripePromise = loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISH_KEY);

const RewardRedeemModal = (props) => {
  const { show, setShow, unpaidRedeem } = props;

  const hasUnpaidRedeem = unpaidRedeem ? true : false;

  const { user, getAuthorizationHeader } = useUserAuth();

  const {
    data: redeemData,
    isLoading: isRedeemListLoading,
    mutate: mutateRedeemList,
  } = useSWR(user ? '/api/v2/me/redeemList' : null, (url) =>
    swrFetcher(url, getAuthorizationHeader)
  );

  const redeemList = _.get(redeemData, 'redeem_list', []);
  const scoreAfterRedeem = _.get(redeemData, 'score_after_redeem', 0);
  const scoreNeeded = _.get(redeemData, 'score_needed', 0);
  // 只有全為平郵寄送，才可選擇平郵
  const isStandardMail = _.every(redeemList, { reward: { delivery_type: 1 } });
  const deliveryTypes = [
    {
      label: '平郵寄送（寄件風險需自行承擔）',
      value: 'standard_mail',
      unit_amount: 10,
    },
    {
      label: '順豐速運',
      value: 'sf_express',
      unit_amount: 30,
    },
  ];

  const getDeliveryLabel = (value) => {
    const found = deliveryTypes.find((d) => d.value === value);
    return found ? found.label : '派送方式';
  };
  // step 0 - get redeem list
  // step 1 - select redeem item
  // step 2 - confirm redeem
  const [step, setStep] = useState(hasUnpaidRedeem ? 2 : 0);
  const [redeemRes, setRedeemRes] = useState(null);
  // {
  //     "redeem": 853,
  //     "current_score": 17218,
  //     "stripe_client_secret": "cs_test_b15XmxBvMEdmT7ZXuTQ4BIxyeDD37xkThZdDvzodNOX78kpIvqzJVAGMmJ_secret_fidwbEhqYWAnPydgaGdgYWFgYScpJ2lkfGpwcVF8dWAnPydocGlxbFpscWBoJyknd2BhbHdgZnFKa0ZqaHVpYHFsamsnPydsY1p3YHRwbHdgYSd4JSUl"
  // }
  const [showStripe, setShowStripe] = useState(hasUnpaidRedeem ? true : false);
  const [redeemError, setRedeemError] = useState(null);

  const removeRewardItem = async (rewardId) => {
    try {
      const headers = await getAuthorizationHeader();
      await api.request({
        url: `/api/v2/me/redeemList/${rewardId}`,
        method: 'DELETE',
        headers,
      });
    } catch (error) {
      console.error(error);
    } finally {
      mutateRedeemList();
    }
  };

  const districtList = _.keys(districts);
  const formRef = useRef();

  const close = () => {
    if (step === 2) {
      window.location.reload();
      return;
    }
    setShow(false);
    setStep(0);
  };

  return (
    <Modal
      isOpen={show}
      onRequestClose={close}
      shouldCloseOnOverlayClick={true}
      className="h-full max-h-none w-full overflow-y-auto rounded-none bg-white outline-none p-safe-offset-0 lg:h-auto lg:max-h-[80vh] lg:w-[480px] lg:rounded-lg"
      overlayClassName="z-[70] absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
    >
      <div className="flex h-full flex-col lg:h-[670px]">
        <div className="h-25 flex flex-row items-center justify-between px-4 pb-6 pt-6 lg:pb-8 lg:pt-5">
          <button
            className="flex h-12 w-12 items-center justify-center rounded-lg border border-[#E7E7E7] p-2.5"
            onClick={close}
          >
            <img
              className="h-5 w-5 object-contain"
              src="/svgs/close.svg"
              alt="close"
            />
          </button>
          <p className="text-lg font-medium text-gray-800">換領清單</p>
          <div className="h-12 w-12"></div>
        </div>
        {step === 0 && (
          <>
            <p className="px-4 pb-1.5 text-xl font-bold text-[#111928]">
              換領產品 ({redeemList.length})
            </p>
            <div className="no-scrollbar min-h-0 grow overflow-auto px-4">
              {redeemList.map((redeem, idx, arr) => {
                const rewardId = _.get(redeem, 'reward.id');
                const rewardCount = _.get(redeem, 'count', 0);
                const rewardScore = _.get(redeem, 'reward.score', 0) || 0;
                const reward = _.get(redeem, 'reward', {});
                const maxPerUser = _.get(reward, 'max_per_user', null);
                const image = _.get(reward, 'image');
                const brandName = _.get(reward, 'brand.name', '');
                const name = _.get(reward, 'name', '');
                const enName = _.get(reward, 'en_name', '');
                return (
                  <div
                    key={redeem.id}
                    className={classnames('flex items-start gap-x-4 py-6', {
                      'border-b border-[#E7E7E7]': idx < arr.length - 1,
                    })}
                  >
                    <div className="h-20 w-20 flex-shrink-0 rounded-lg bg-[#F5F5F5] bg-opacity-50 p-1">
                      <img
                        src={
                          image ? image.url : '/images/placeholders/product.png'
                        }
                        className="h-full w-full object-contain"
                      ></img>
                    </div>
                    <div className="flex min-w-0 grow">
                      <div className="flex min-w-0 grow flex-col gap-y-1">
                        {brandName && (
                          <p className="text-sm text-[#4A4A4A]">{brandName}</p>
                        )}
                        {enName && (
                          <p className="text-lg font-medium text-[#4A4A4A]">
                            {enName}
                          </p>
                        )}
                        {name && (
                          <p className="text-lg font-medium text-[#4A4A4A]">
                            {name}
                          </p>
                        )}
                        {!_.isNil(maxPerUser) && (
                          <p className="text-sm text-[#9CA3AF]">
                            (每人限換 {maxPerUser} 件)
                          </p>
                        )}
                        <div className="mt-2 flex text-base">
                          <img
                            src="/svgs/sorra-points-small.svg"
                            className="mr-2"
                          ></img>
                          <p className="font-bold">{rewardScore}</p>
                        </div>
                      </div>
                      <div className="flex w-6 flex-shrink-0 flex-col justify-between">
                        <div className="flex h-6 w-6 items-center justify-center rounded-full bg-sorra-pink">
                          <p className="text-sm font-medium text-white">
                            {rewardCount}
                          </p>
                        </div>
                        <button
                          onClick={() => {
                            removeRewardItem(rewardId);
                          }}
                        >
                          <img
                            src="/svgs/trash.svg"
                            className="h-6 w-6"
                            alt=""
                          />
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
              <button
                className="mb-12 w-full rounded-lg border border-sorra-pink bg-white px-5 py-3 text-base font-medium text-sorra-pink"
                onClick={() => {
                  window.location = '/rewards';
                }}
              >
                加入更多產品
              </button>
            </div>
            <div
              style={{
                boxShadow:
                  '0px -4px 6px -1px rgba(0, 0, 0, 0.10), 0px 2px 4px -2px rgba(0, 0, 0, 0.05)',
              }}
              className="flex flex-col items-center px-4 pb-6 pt-4 text-base font-medium"
            >
              <div className="flex w-full items-center justify-between">
                <p>使用積分</p>
                <div className="flex">
                  <img
                    src="/svgs/sorra-points-small.svg"
                    className="mr-2"
                  ></img>
                  <p className="font-bold">{scoreNeeded}</p>
                </div>
              </div>
              <div className="mt-3.5 flex w-full items-center justify-between">
                <p>使用後將剩餘積分</p>
                <div className="flex">
                  <img
                    src="/svgs/sorra-points-small.svg"
                    className="mr-2"
                  ></img>
                  <p className="font-bold">{scoreAfterRedeem}</p>
                </div>
              </div>
              <div className="mt-3.5 w-full">
                <div
                  className={
                    'flex flex-row items-end justify-end text-sm font-normal text-[#9B1C1C]' +
                    (scoreAfterRedeem >= 0 ? ' opacity-[0]' : '')
                  }
                >
                  <img src="/svgs/error.svg" className="mx-[8px]"></img>
                  積分不足，請調整換領清單
                </div>
              </div>
              <button
                className="mt-8 w-full rounded-lg bg-sorra-pink px-5 py-3 text-white disabled:bg-gray-100 lg:w-[343px]"
                onClick={() => {
                  setStep(1);
                }}
                disabled={redeemList.length <= 0 || scoreAfterRedeem < 0}
              >
                確認換領
              </button>
            </div>
          </>
        )}
        {step === 1 && (
          <Formik
            innerRef={formRef}
            enableReinitialize
            validateOnMount={true}
            initialValues={{
              shipping_fee: isStandardMail ? 'HKD$10' : 'HKD$30',
              receiver: '',
              phone: '',
              district: '',
              sub_district: '',
              address: '',
              delivery_type: isStandardMail ? 'standard_mail' : 'sf_express',
            }}
            validationSchema={Yup.object({
              receiver: Yup.string().required('Required'),
              phone: Yup.string().required('Required'),
              delivery_type: Yup.string().required('Required'),
              address: Yup.string().required('Required'),
              district: Yup.string().when('delivery_type', {
                is: 'sf_express',
                then: (schema) => schema.required('Required'),
                otherwise: (schema) => schema.notRequired(),
              }),
              sub_district: Yup.string().when('delivery_type', {
                is: 'sf_express',
                then: (schema) => schema.required('Required'),
                otherwise: (schema) => schema.notRequired(),
              }),
            })}
            onSubmit={async (values, actions) => {
              // console.log({ values, actions });
              setRedeemRes(null);
              setRedeemError(null);

              const headers = await getAuthorizationHeader();
              try {
                const { data } = await api.request({
                  method: 'POST',
                  url: '/api/v2/me/redeem',
                  data: values,
                  headers,
                });
                setRedeemRes(data);
                setStep(2);
                if (data.stripe_client_secret) {
                  setShowStripe(true);
                }
              } catch (error) {
                setRedeemError(error);
              }
            }}
          >
            {(props) => {
              const {
                values,
                errors,
                handleBlur,
                handleChange,
                handleSubmit,
                isSubmitting,
                isValid,
              } = props;
              const {
                receiver,
                address,
                district,
                sub_district,
                delivery_type,
                shipping_fee,
              } = values;
              return (
                <>
                  <p className="px-4 pb-1.5 text-xl font-bold text-[#111928]">
                    換領資料
                  </p>
                  <div className="no-scrollbar min-h-0 grow overflow-auto px-4">
                    <form
                      className="flex w-full flex-col"
                      onSubmit={handleSubmit}
                    >
                      <div className="mt-6 text-base">收件人</div>
                      <div className="mt-2">
                        <div className="h-12 w-full rounded-lg border border-[#E7E7E7]">
                          <input
                            className="h-full w-full rounded-lg border-none px-2 focus:ring-0"
                            value={receiver}
                            type="text"
                            name="receiver"
                            onChange={handleChange}
                          />
                        </div>
                      </div>
                      <div className="mt-6 text-base">收件人電話 (+852)</div>
                      <div className="mt-2">
                        <div className="h-12 w-full rounded-lg border border-[#E7E7E7]">
                          <input
                            className="h-full w-full rounded-lg border-none px-[8px] focus:ring-0"
                            value={values.phone}
                            type="text"
                            name="phone"
                            onChange={handleChange}
                          />
                        </div>
                      </div>

                      <div className="mt-6 space-y-2">
                        <div className="text-base">
                          派送方式{isStandardMail ? ' (可選)' : ''}
                        </div>
                        <Listbox
                          value={delivery_type}
                          onChange={(v) => {
                            handleChange({
                              target: {
                                name: 'delivery_type',
                                value: v,
                              },
                            });
                            handleChange({
                              target: {
                                name: 'sub_district',
                                value: '',
                              },
                            });
                            handleChange({
                              target: {
                                name: 'district',
                                value: '',
                              },
                            });
                            handleChange({
                              target: {
                                name: 'shipping_fee',
                                value:
                                  'HKD$' +
                                  (_.find(deliveryTypes, { value: v })
                                    .unit_amount || 0),
                              },
                            });
                          }}
                          disabled={!isStandardMail}
                        >
                          <div className="relative mt-2">
                            <ListboxButton className="relative h-12 w-full cursor-default rounded-lg border border-[#E7E7E7] bg-white px-2 text-left">
                              <span
                                className={classnames('block truncate', {
                                  'cursor-not-allowed': !isStandardMail,
                                })}
                              >
                                {getDeliveryLabel(delivery_type) || '派送方式'}
                              </span>
                              <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                <ChevronUpDownIcon
                                  aria-hidden="true"
                                  className="h-5 w-5 text-gray-400"
                                />
                              </span>
                            </ListboxButton>
                            <ListboxOptions
                              // transition
                              className="sm:text-sm absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                            >
                              {deliveryTypes.map((d, idx) => (
                                <ListboxOption
                                  key={d.value}
                                  value={d.value}
                                  className="group relative cursor-pointer py-2 pl-3 pr-9"
                                >
                                  {({ selected }) => (
                                    <>
                                      <span
                                        className={classnames(
                                          'block truncate',
                                          {
                                            'font-semibold': selected,
                                          }
                                        )}
                                      >
                                        {d.label}
                                      </span>
                                      {selected && (
                                        <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-[#F16B7C]">
                                          <svg
                                            className="h-5 w-5"
                                            viewBox="0 0 20 20"
                                            fill="currentColor"
                                          >
                                            <path
                                              fillRule="evenodd"
                                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                              clipRule="evenodd"
                                            />
                                          </svg>
                                        </span>
                                      )}
                                    </>
                                  )}
                                </ListboxOption>
                              ))}
                            </ListboxOptions>
                          </div>
                        </Listbox>
                      </div>
                      {delivery_type === 'sf_express' && (
                        <div className="mt-6 space-y-2">
                          <div className="text-base">派送地區</div>
                          <Listbox
                            value={district}
                            onChange={(v) => {
                              handleChange({
                                target: {
                                  name: 'sub_district',
                                  value: '',
                                },
                              });
                              handleChange({
                                target: {
                                  name: 'district',
                                  value: v,
                                },
                              });
                            }}
                          >
                            <div className="relative mt-2">
                              <ListboxButton className="relative h-12 w-full cursor-default rounded-lg border border-[#E7E7E7] bg-white px-2 text-left">
                                <span
                                  className={classnames('block truncate', {
                                    'text-gray-500': !district,
                                  })}
                                >
                                  {district || '派送地區'}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon
                                    aria-hidden="true"
                                    className="h-5 w-5 text-gray-400"
                                  />
                                </span>
                              </ListboxButton>
                              <ListboxOptions
                                // transition
                                className="sm:text-sm absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                              >
                                {districtList.map((d, idx) => (
                                  <ListboxOption
                                    key={d}
                                    value={d}
                                    className="group relative cursor-pointer py-2 pl-3 pr-9"
                                  >
                                    <span className="block truncate">{d}</span>
                                  </ListboxOption>
                                ))}
                              </ListboxOptions>
                            </div>
                          </Listbox>
                        </div>
                      )}
                      {delivery_type === 'sf_express' && (
                        <div className="mt-6 space-y-2">
                          <div className="text-base">派送區域</div>
                          <Listbox
                            value={sub_district}
                            onChange={(v) => {
                              // setSubDistrict(v);
                              handleChange({
                                target: {
                                  name: 'sub_district',
                                  value: v,
                                },
                              });
                            }}
                            disabled={!district}
                          >
                            <div className="relative mt-2">
                              <ListboxButton className="relative h-12 w-full cursor-default rounded-lg border border-[#E7E7E7] bg-white px-2 text-left">
                                <span
                                  className={classnames('block truncate', {
                                    'text-gray-500': !sub_district,
                                    'cursor-not-allowed': !district,
                                  })}
                                >
                                  {sub_district || '派送區域'}
                                </span>
                                <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                                  <ChevronUpDownIcon
                                    aria-hidden="true"
                                    className="h-5 w-5 text-gray-400"
                                  />
                                </span>
                              </ListboxButton>
                              <ListboxOptions
                                // transition
                                className="sm:text-sm absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none data-[closed]:data-[leave]:opacity-0 data-[leave]:transition data-[leave]:duration-100 data-[leave]:ease-in"
                              >
                                {district &&
                                  districts[district].map((d, idx) => (
                                    <ListboxOption
                                      key={d}
                                      value={d}
                                      className="group relative cursor-pointer py-2 pl-3 pr-9"
                                    >
                                      <span className="block truncate">
                                        {d}
                                      </span>
                                    </ListboxOption>
                                  ))}
                              </ListboxOptions>
                            </div>
                          </Listbox>
                        </div>
                      )}
                      <div className="mt-6 block text-base">
                        {delivery_type == 'sf_express' ? (
                          '地址 (上門地址／順豐自取點)'
                        ) : (
                          <>
                            平郵地址
                            <br />◎ 請填寫可投遞郵件之平郵地址，不可使用郵站。
                          </>
                        )}
                      </div>
                      <div className="mt-2">
                        <div className="h-12 w-full rounded-lg border border-[#E7E7E7]">
                          <input
                            className="h-full w-full rounded-lg border-none px-2 focus:ring-0"
                            value={address}
                            name="address"
                            type="text"
                            onChange={handleChange}
                            placeholder={
                              delivery_type == 'sf_express'
                                ? '地址 (上門地址／順豐自取點)'
                                : '平郵地址'
                            }
                          />
                        </div>
                      </div>
                      <div className="mt-6 text-base">
                        運費（將於下一步收取）
                      </div>
                      <div className="mt-2">
                        <div className="h-12 w-full rounded-lg border border-[#E7E7E7]">
                          <input
                            name="shipping_fee"
                            value={shipping_fee}
                            className="h-full w-full cursor-not-allowed rounded-lg border-none px-2 !text-[#111928] opacity-100 focus:outline-none focus:ring-0"
                            style={{ WebkitTextFillColor: '#111928' }}
                            type="text"
                            disabled
                          />
                        </div>
                      </div>
                      <div className="my-8">
                        <div className="flex flex-row">
                          <span className="text-[#9B9B9B]">
                            現時僅限香港用戶參與換領。確認即表示您了解並同意Sorra
                            <a
                              className="underline"
                              target="_blank"
                              href="https://sorra.notion.site/Website-Terms-and-Conditions-e99d70976b7842c38495ee97a6074f13"
                            >
                              服務條款
                            </a>
                            及
                            <a
                              className="underline"
                              target="_blank"
                              href="https://sorra.notion.site/Private-Policy-sorra-net-14-Aug-2023-c8b664a4d40a42f0b35af9e4f416357a?pvs=4"
                            >
                              私隱政策
                            </a>
                          </span>
                        </div>
                      </div>
                    </form>
                  </div>
                  {redeemError && (
                    <div className="mt-2.5 flex flex-row items-center justify-center text-right text-sm font-normal text-[#9B1C1C]">
                      <img src="/svgs/error.svg" className="mx-2"></img>
                      積分不足，請調整換領清單
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4 px-4 py-8 lg:py-7">
                    <button
                      className="h-12 w-full justify-self-end rounded-lg border border-sorra-pink px-4 py-3 text-sorra-pink lg:w-[163px]"
                      onClick={() => {
                        setShow(false);
                      }}
                    >
                      返回
                    </button>
                    <button
                      className="h-12 w-full justify-self-start rounded-lg bg-sorra-pink px-4 py-3 text-white disabled:bg-gray-100 lg:w-[163px]"
                      disabled={!isValid || isSubmitting}
                      onClick={() => {
                        formRef.current.handleSubmit();
                      }}
                    >
                      確認
                    </button>
                  </div>
                </>
              );
            }}
          </Formik>
        )}
        {step === 2 && (
          <div className="flex grow flex-col items-center justify-center px-8">
            <img
              src="/svgs/complete-gift.svg"
              className="h-25 w-25 mx-auto lg:h-32 lg:w-32"
            />
            <div className="mt-7 text-center text-xl font-bold text-sorra-gray-900">
              確認換取禮物
            </div>
            <div className="mt-7 text-center text-sm font-normal text-sorra-gray-800">
              已經寄出確認電郵，產品好快送到你手上
              <br />
              你尚有 ${redeemRes ? redeemRes.current_score : user.score} 積分！
            </div>
            <button
              className="mt-8 flex h-12 w-full items-center justify-center rounded-lg bg-sorra-pink px-5 py-3 lg:w-[311px]"
              onClick={() => {
                window.location = '/profile/redeem';
              }}
            >
              <p className="text-base text-white">兌換記錄</p>
            </button>
          </div>
        )}
      </div>
      <Modal
        isOpen={showStripe}
        onRequestClose={() => {
          setShowStripe(false);
        }}
        shouldCloseOnOverlayClick={false}
        className="h-full max-h-none w-full overflow-y-auto rounded-none bg-white px-4 pb-8 pt-3 outline-none lg:pb-10 lg:pt-10"
        overlayClassName="z-[80] absolute flex items-center justify-center top-0 left-0 right-0 bottom-0 w-full h-full bg-[#111928] bg-opacity-50"
      >
        <EmbeddedCheckoutProvider
          stripe={stripePromise}
          options={{
            fetchClientSecret: async () => {
              const targetRedeemId = hasUnpaidRedeem
                ? _.get(unpaidRedeem, 'id')
                : _.get(redeemRes, 'redeem', null);
              try {
                const headers = await getAuthorizationHeader();
                const { data } = await api.request({
                  url: `/api/v3/retrieveStripePaymentSession`,
                  method: 'GET',
                  params: {
                    redeemId: targetRedeemId,
                  },
                  headers: {
                    ...headers,
                  },
                });
                const { stripe_client_secret } = data;
                return stripe_client_secret;
              } catch (error) {
                console.log('error', error);
                window.location.reload();
              }
              return null;
            },
            onComplete: () => {
              setShowStripe(false);
            },
          }}
        >
          <EmbeddedCheckout />
        </EmbeddedCheckoutProvider>
      </Modal>
    </Modal>
  );
};

export default RewardRedeemModal;
